import { useEffect, useMemo, useState } from 'react'
import { Countries } from '../data/countries'
import {
  ConsumerAuthMethodEnum,
  useConsumerAccessRequestMutation,
  useConsumerVerifyCodeMutation,
  useTradeInConfigQuery,
  useWholesaleConsumerCreateMutation,
} from '../generated/graphql'
import type { CSSProperties } from 'react'
import { BannerVideo } from '../components/BannerVideo'
import { FrontPageVideo } from '../components/FrontPageVideo'
import { useTranslation } from '../localization/useTranslation'
import {
  getBanner,
  getStoreCountry,
  getStoreCurrency,
  getBannerRotateSeconds,
  getVideo,
  nextBannerImage,
  orderBannerImages,
  resolveBannerVideo,
  resolveProductSearchEnabled,
} from '../providers/embedConfig'
import { useAuthStore } from '../store/auth'
import { StepArt } from '../ui/stepArt'
import { cssLength } from '../util/cssLength'
import { scrollWidgetToTop } from '../util/scroll'

/**
 * Brand overrides for the layer *underneath* the banner images, from `tradeInConfig.banner`: the
 * configured gradient, else a flat colour, else (unset) the theme's own `--fp-banner` gradient. This
 * is what a visitor looks at while the images decode and what remains if every image URL is dead, so
 * it is always painted — the images composite over it. The scrim is independent of both.
 *
 * Also the banner's `minHeight`. Being inline, it beats the phone rule that lets the banner shrink to
 * the login card, so a width-relative value (`cqw`) is the one that suits every screen.
 */
function bannerStyles(): { banner?: CSSProperties; scrim?: CSSProperties } {
  const b = getBanner()
  if (!b) return {}

  const banner: CSSProperties = {}
  if (b.minHeight != null) banner.minHeight = cssLength(b.minHeight)
  if (b.gradient) banner.backgroundImage = b.gradient
  else if (b.color) {
    banner.backgroundImage = 'none'
    banner.backgroundColor = b.color
  }
  const scrim = b.scrim != null ? { background: `rgba(15,18,26,${b.scrim})` } : undefined
  return { banner: Object.keys(banner).length ? banner : undefined, scrim }
}

/**
 * `background-image` for one banner layer, with the quote/escape characters that could break out
 * stripped, anchored at the brand's `banner.position` when it sets one (CSS keeps `center` otherwise).
 */
const bannerLayerStyle = (url: string, position?: string): CSSProperties => ({
  backgroundImage: `url("${url.replace(/["\\]/g, '')}")`,
  ...(position ? { backgroundPosition: position } : {}),
})

/**
 * The visitor's browser settings that rule out an auto-playing banner video. `saveData` comes from the
 * Network Information API, which only Chromium ships (and TypeScript's DOM types leave out).
 */
function viewerMediaPrefs(): { reducedMotion: boolean; saveData: boolean } {
  const { connection } = navigator as Navigator & { connection?: { saveData?: boolean } }
  return {
    reducedMotion:
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    saveData: connection?.saveData === true,
  }
}

/** Add a URL to a set-valued state, leaving the reference alone when it's already in there. */
const remember =
  (url: string) =>
  (current: ReadonlySet<string>): ReadonlySet<string> =>
    current.has(url) ? current : new Set(current).add(url)

/**
 * Cross-fade through the configured banner images, one every `rotateSeconds`.
 *
 * Only images that have actually decoded are ever shown, so a dead URL is skipped in silence rather
 * than fading the banner to a blank panel — and if none of them load, nothing is rendered over the
 * gradient underneath. Two layers come back: `current` fades in over `previous`, which holds at full
 * opacity until the fade finishes. Fading only the incoming layer is what keeps the gradient from
 * flashing through the middle of the transition, where two half-faded images would otherwise leave
 * the stack partly transparent.
 */
function useBannerImages(): { previous?: string; current?: string } {
  const configured = getBanner()?.image
  const [date] = useState(() => new Date())
  const images = useMemo(() => orderBannerImages(configured, date), [configured, date])
  const [decoded, setDecoded] = useState<ReadonlySet<string>>(() => new Set())
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set())
  const [shown, setShown] = useState<{ previous?: string; current?: string }>({})

  // Decode every candidate up front, tracking both outcomes: a decoded image can be shown, and a
  // failed one lets the picture below move up rather than waiting on a URL that is never arriving.
  useEffect(() => {
    let active = true
    const probes = images.map((url) => {
      const probe = new Image()
      probe.onload = () => active && setDecoded(remember(url))
      probe.onerror = () => active && setFailed(remember(url))
      probe.src = url
      return probe
    })

    return () => {
      active = false
      probes.forEach((probe) => {
        probe.onload = null
        probe.onerror = null
      })
    }
  }, [images])

  const usable = useMemo(() => images.filter((url) => decoded.has(url)), [images, decoded])
  const rotateSeconds = getBannerRotateSeconds()

  // The image to open on: the day's lead, or the first one behind it that hasn't failed. Deliberately
  // not "the first one that happens to have decoded" — skipping past an image still in flight paints a
  // picture that gets yanked away the moment the slower lead lands, which on a throttled connection
  // reads as the banner flickering to the wrong image and then cross-fading off it.
  const head = images.find((url) => !failed.has(url))
  const opening = head && decoded.has(head) ? head : undefined

  // Track the pair by URL rather than by index so an image joining the set late can never yank the
  // picture currently on screen; anything no longer usable falls back to the opening image.
  const current = shown.current && usable.includes(shown.current) ? shown.current : opening
  const previous =
    shown.previous && shown.previous !== current && usable.includes(shown.previous)
      ? shown.previous
      : undefined

  // Each image on screen schedules its own successor, so the hold is measured from the moment one
  // actually appears — nothing rotates before there is something to rotate away from.
  useEffect(() => {
    if (!current || usable.length < 2) return
    // Reduced motion gets the opening image and no auto-changing content at all: the global
    // `prefers-reduced-motion` rule zeroes our fade, which would turn each rotation into a hard cut.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const timer = window.setTimeout(
      () => setShown({ previous: current, current: nextBannerImage(usable, current) }),
      rotateSeconds * 1000,
    )
    return () => window.clearTimeout(timer)
  }, [current, usable, rotateSeconds])

  return { previous, current }
}

/**
 * Public front page (login) — shown when a shopId is present but no accessToken yet. Ported from
 * v1's PublicFrontPage/Jane Konig banner layout: a full-bleed brand banner (the brand's images and
 * video from `tradeInConfig.banner`, else the theme gradient) with a centred card, plus a "how it
 * works" strip.
 *
 * Which login methods appear is driven entirely by the shop's `TradeInConfig.authMethods`
 * (mirrors v1's CodeLoginInline): the supported methods are SMS code and email login-link, and we
 * only offer those the backend lists. When the shop enables both, the customer first picks one;
 * when it enables one, that form shows directly. Wholesale is a separate top-level option offered
 * when the shop enables it. SMS/wholesale yield an accessToken client-side (→ `setLoginToken`
 * advances the gate); the email link returns via `?login=`.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** The login methods this page can render, keyed to Core's `ConsumerAuthMethodEnum`. */
type LoginMethod = 'sms' | 'email'

/** Default phone-prefix from the storefront country (mirrors v1's detectDefaultPhonePrefix); DK fallback. */
function detectPhonePrefix(): string {
  const cc = getStoreCountry()
  if (cc) {
    const match = Countries.find((c) => c.ISO === cc.toUpperCase())
    if (match) return match.PhonePrefix
  }
  return '45'
}

export function PublicFrontPage({ shopId }: { shopId: string }) {
  const { t, locale } = useTranslation()
  const setLoginToken = useAuthStore((s) => s.setLoginToken)
  const mergeConfig = useAuthStore((s) => s.mergeConfig)

  const { data, error } = useTradeInConfigQuery({ variables: { shopId }, fetchPolicy: 'cache-first' })
  const cfg = data?.TradeInConfig
  // Seed the voucher strategy into the store as soon as config arrives, so the login page's "how it
  // works" step 3 (a strategy-variant string) reads correctly before the customer has logged in.
  useEffect(() => {
    if (cfg?.voucherStrategy) mergeConfig({ voucherStrategy: cfg.voucherStrategy })
  }, [cfg?.voucherStrategy, mergeConfig])
  // Which login options to show is entirely config-driven, so we must not render the form before
  // config arrives — otherwise the email fallback flashes for a beat, then swaps to the real set.
  // Show a skeleton until config resolves; on error, fall through to the graceful email fallback.
  const configReady = Boolean(cfg) || Boolean(error)
  const authMethods = (cfg?.authMethods ?? []).filter(Boolean) as string[]
  // Backend-driven: only the methods the shop enables, in a stable display order. A shop that lists
  // neither still needs a way in, so fall back to the email login-link.
  const hasSms = authMethods.includes(ConsumerAuthMethodEnum.SmsCode)
  const hasMailLink = authMethods.includes(ConsumerAuthMethodEnum.MailLink)
  const offered: LoginMethod[] = [
    ...(hasSms ? (['sms'] as const) : []),
    ...(hasMailLink ? (['email'] as const) : []),
  ]
  const methods: LoginMethod[] = offered.length ? offered : ['email']
  const multiMethod = methods.length > 1
  const showWholesale = Boolean(
    cfg?.wholeSaleActive || resolveProductSearchEnabled(cfg?.productSearchEnabled),
  )

  // `select` = the method picker (only reachable when >1 method); otherwise the active method's form.
  const [mode, setMode] = useState<'select' | LoginMethod>('select')
  const [email, setEmail] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const [phonePrefix, setPhonePrefix] = useState(detectPhonePrefix)
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Config loads async: land on the picker when the shop offers several methods, else on the single
  // method's form. Re-runs if the offered set changes once config arrives.
  const methodsKey = methods.join(',')
  useEffect(() => {
    setMode(multiMethod ? 'select' : methods[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methodsKey])

  const pickMethod = (m: LoginMethod) => {
    setErr(null)
    setMode(m)
  }
  const backToSelect = () => {
    setErr(null)
    setCodeSent(false)
    setMode('select')
  }
  // Wholesale is a top-level alternative, shown at an entry point only: the picker, or a single
  // method's first screen — never once the customer is mid-verification (code sent / link sent).
  const atEntry = mode === 'select' || (!multiMethod && !codeSent && !emailSent)

  const [accessRequest] = useConsumerAccessRequestMutation()
  const [verifyCode] = useConsumerVerifyCodeMutation()
  const [createWholesale] = useWholesaleConsumerCreateMutation()

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setErr(null)
    try {
      await fn()
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('login.failed'))
    } finally {
      setBusy(false)
    }
  }

  const submitEmail = () =>
    run(async () => {
      if (!EMAIL_RE.test(email.trim())) {
        setErr(t('login.emailInvalid'))
        return
      }
      await accessRequest({
        variables: {
          shopId,
          identifier: email.trim().toLowerCase(),
          authMethod: ConsumerAuthMethodEnum.MailLink,
        },
      })
      setEmailSent(true)
    })

  // Core expects a full international number; combine the prefix + national part (mirrors v1).
  const fullPhone = () => `+${phonePrefix} ${phone.trim()}`

  const submitSendCode = () =>
    run(async () => {
      if (phone.trim().length < 6) {
        setErr(t('login.phoneInvalid'))
        return
      }
      await accessRequest({
        variables: {
          shopId,
          identifier: fullPhone(),
          authMethod: ConsumerAuthMethodEnum.SmsCode,
          onlyDefaultFlow: false,
        },
      })
      setCodeSent(true)
    })

  const submitVerify = () =>
    run(async () => {
      const res = await verifyCode({ variables: { shopId, phone: fullPhone(), code: code.trim() } })
      const token = res.data?.ConsumerVerifyCode?.accessToken
      if (!token) {
        setErr(t('login.codeInvalid'))
        return
      }
      setLoginToken(token) // → the App gate advances to AuthProvider
    })

  const startWholesale = () =>
    run(async () => {
      const res = await createWholesale({
        variables: {
          shopId,
          locale,
          country: getStoreCountry(),
          currency: getStoreCurrency(),
        },
      })
      const token = res.data?.ConsumerCreate?.accessToken
      // We just created this consumer (selfCreated) — flag it so AuthProvider skips the order-feed
      // import (they have no past orders).
      if (token) {
        setLoginToken(token, true)
        // This replaces the tall public front page without a route change, so bring the newly
        // entered app back into view. Normal in-app navigation remains owned by FlowLayout.
        scrollWidgetToTop()
      }
    })

  // A "back to the method picker" link, shown only when the shop offers more than one method.
  const renderBack = () =>
    multiMethod ? (
      <button type="button" className="link fp-back" onClick={backToSelect}>
        {t('login.back')}
      </button>
    ) : null

  const renderLogin = () => {
    if (emailSent) {
      return (
        <div className="fp-sent">
          <h3>{t('login.emailSentTitle')}</h3>
          <p>{t('login.emailSentBody', { email })}</p>
        </div>
      )
    }

    // Method picker — reached only when the shop enables more than one login method.
    if (mode === 'select') {
      return (
        <div className="fp-methods">
          {hasSms && (
            <button type="button" className="fp-method" onClick={() => pickMethod('sms')}>
              <span className="fp-method-title">{t('login.methodSms')}</span>
              <span className="fp-method-desc">{t('login.methodSmsDesc')}</span>
            </button>
          )}
          {hasMailLink && (
            <button type="button" className="fp-method" onClick={() => pickMethod('email')}>
              <span className="fp-method-title">{t('login.methodEmail')}</span>
              <span className="fp-method-desc">{t('login.methodEmailDesc')}</span>
            </button>
          )}
        </div>
      )
    }

    if (mode === 'sms' && codeSent) {
      return (
        <form
          className="fp-form"
          onSubmit={(e) => {
            e.preventDefault()
            submitVerify()
          }}
        >
          <label htmlFor="fp-code">{t('login.codeLabel')}</label>
          <input
            id="fp-code"
            className="fp-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('login.codePlaceholder')}
          />
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t('login.working') : t('login.verify')}
          </button>
          <button type="button" className="link fp-back" onClick={() => setCodeSent(false)}>
            {t('login.changePhone')}
          </button>
        </form>
      )
    }

    if (mode === 'sms') {
      return (
        <form
          className="fp-form"
          onSubmit={(e) => {
            e.preventDefault()
            submitSendCode()
          }}
        >
          <label htmlFor="fp-phone">{t('login.phoneLabel')}</label>
          <div className="fp-phone">
            <select
              className="fp-input fp-prefix"
              aria-label={t('details.prefix')}
              value={phonePrefix}
              onChange={(e) => setPhonePrefix(e.target.value)}
            >
              {Countries.map((c) => (
                <option key={c.ISO} value={c.PhonePrefix}>
                  {c.ISO} +{c.PhonePrefix}
                </option>
              ))}
            </select>
            <input
              id="fp-phone"
              className="fp-input fp-num"
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t('login.phonePlaceholder')}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t('login.working') : t('login.sendCode')}
          </button>
          {renderBack()}
        </form>
      )
    }

    // mode === 'email' — the email login-link (MAIL_LINK).
    return (
      <form
        className="fp-form"
        onSubmit={(e) => {
          e.preventDefault()
          submitEmail()
        }}
      >
        <label htmlFor="fp-email">{t('login.emailLabel')}</label>
        <input
          id="fp-email"
          className="fp-input"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('login.emailPlaceholder')}
        />
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? t('login.working') : t('login.start')}
        </button>
        {renderBack()}
      </form>
    )
  }

  const STEPS = ['step1', 'step2', 'step3']

  const { banner: bannerStyle, scrim: scrimStyle } = bannerStyles()
  const { previous: previousBanner, current: currentBanner } = useBannerImages()
  const bannerPosition = getBanner()?.position?.trim()
  const bannerVideo = resolveBannerVideo(getBanner()?.video, viewerMediaPrefs())
  // Optional brand override for the "how it works" section: a hero video. (Custom step icons are the
  // other one — StepArt reads those straight from the embed config.)
  const videoUrl = getVideo()

  return (
    <div className="fp">
      <div className="fp-banner" style={bannerStyle}>
        {previousBanner && (
          <div className="fp-banner-img" style={bannerLayerStyle(previousBanner, bannerPosition)} />
        )}
        {/* Keyed by URL so each new image mounts fresh and replays the fade-in animation. */}
        {currentBanner && (
          <div
            key={currentBanner}
            className="fp-banner-img fp-banner-img-in"
            style={bannerLayerStyle(currentBanner, bannerPosition)}
          />
        )}
        {bannerVideo && <BannerVideo key={bannerVideo} url={bannerVideo} position={bannerPosition} />}
        <div className="fp-scrim" style={scrimStyle} />
        {/* No language selector here: pre-login language follows the storefront (see
            resolveDefaultLocale). Its own selector handles it; the in-app switcher takes over later. */}
        <div className="fp-card">
          <h1>{t('login.title')}</h1>
          <p className="fp-sub">{t('login.subtitle')}</p>

          {configReady ? (
            <>
              {renderLogin()}
              {err && <div className="fp-err">{err}</div>}

              {showWholesale && atEntry && (
                <>
                  <div className="fp-or">{t('login.or')}</div>
                  <button
                    type="button"
                    className="btn btn-ghost fp-wholesale"
                    onClick={startWholesale}
                    disabled={busy}
                  >
                    {t('login.wholesale')}
                  </button>
                </>
              )}
            </>
          ) : (
            <LoginSkeleton />
          )}
        </div>
      </div>

      <div className="fp-steps">
        {STEPS.map((key, i) => (
          <div className="fp-step" key={key}>
            <StepArt index={i} className="fp-step-n" />
            <h4>{t(`login.${key}Title`)}</h4>
            <p>{t(`login.${key}Body`)}</p>
            {i < STEPS.length - 1 && <span className="fp-step-line" aria-hidden />}
          </div>
        ))}
      </div>

      {videoUrl && <FrontPageVideo url={videoUrl} />}
    </div>
  )
}

/**
 * Placeholder shown inside the login card while `TradeInConfig` loads, so the config-driven login
 * options never flash a wrong layout first. Neutral bars sized to the card's interactive area —
 * they fit whichever login (method picker / SMS / email + wholesale) turns out to be shown.
 */
function LoginSkeleton() {
  return (
    <div className="fp-skeleton" aria-hidden>
      <span className="sk sk-line" />
      <span className="sk sk-block" />
      <span className="sk sk-block" />
      <span className="sk sk-btn" />
    </div>
  )
}
